import type { StaffPosition } from '../../utils/positionTypes';
import type { WorkflowRoles } from '../../utils/documentWorkflowRules';

/**
 * The raw facts loaded once per session by <RolesProvider>. Every role flag the
 * UI uses is derived from these by the pure functions below, so the rules can
 * be unit-tested without React.
 */
export interface RawRoles {
  /** profiles.is_admin (false when the read failed). */
  isAdmin: boolean;
  /** profiles.is_active (false when the read failed). */
  isActive: boolean;
  /** The caller's active staff_positions rows ([] when the read failed). */
  positions: StaffPosition[];
  /** is_keystone_member(uid) RPC (false when the call failed). */
  keystoneMember: boolean;
  /** is_keystone_leader(uid) RPC (false when the call failed). */
  keystoneLeader: boolean;
}

export interface Roles extends WorkflowRoles {
  isActive: boolean;
  positions: StaffPosition[];
  isLdAssistant: boolean;
  isKeystoneMember: boolean;
  isKeystoneLeader: boolean;
  canAccessDocuments: boolean;
  hasPosition: (type: string) => boolean;
}

export const EMPTY_RAW_ROLES: RawRoles = {
  isAdmin: false,
  isActive: false,
  positions: [],
  keystoneMember: false,
  keystoneLeader: false,
};

export function deriveRoles(raw: RawRoles): Roles {
  const hasPosition = (type: string) =>
    raw.positions.some((p) => p.positionType === type && p.isActive);
  const isLdAssistant = hasPosition('ld_assistant');
  return {
    isAdmin: raw.isAdmin,
    isActive: raw.isActive,
    positions: raw.positions,
    isLdAdmin: hasPosition('ld_admin_assistant'),
    isIpl: hasPosition('inclusive_practice_leader'),
    isLdAssistant,
    isKeystoneMember: raw.keystoneMember,
    isKeystoneLeader: raw.keystoneLeader,
    // LD Assistants cannot access documents; any other position, or none, can.
    canAccessDocuments: !isLdAssistant,
    hasPosition,
  };
}

// Projections used by the legacy hooks. Each returns exactly the shape the
// hook returned before the roles context existed.

export const selectIsAdmin = (r: Roles) => ({ isAdmin: r.isAdmin });

export const selectDocumentAccess = (r: Roles) => ({
  canAccessDocuments: r.canAccessDocuments,
});

export const selectWorkflowRoles = (r: Roles): WorkflowRoles => ({
  isAdmin: r.isAdmin,
  isLdAdmin: r.isLdAdmin,
  isIpl: r.isIpl,
});

export const selectKeystoneAccess = (r: Roles) => ({
  isMember: r.isKeystoneMember,
  isLeader: r.isKeystoneLeader,
});
